import { Box, Heading, List, ResponsiveContext, Text } from "grommet";
import React, { useContext, useState } from "react";
import Section from "../../../components/Section";
import CustomersPage from "../index";
import { formatDate } from "../../../utils/dates";
import { useRouter } from "next/router";
import PreviewEmailModal from "../../../components/PreviewEmailModal";
import { DialogContext } from "../../../components/DialogProvider";
import { NotificationsContext } from "../../../components/NotificationsProvider";
import { handleErrors, nonRedirectFetcher } from "../../../utils/errors";
import { archiveCustomers, notifyCustomer } from "../../../utils/customers";
import useSWR, { useSWRConfig } from "swr";
import StatusIcon from "../../../components/StatusIcon";
import {
    faCircleCheck as IconCircleCheck,
    faHourglassEnd as IconLoadingCircle,
    faTrashCan as IconDelete,
} from '@fortawesome/pro-light-svg-icons';
import DataTable from "../../../components/DataTable";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";
import CopyLinkWidget from "../../../components/CopyLinkWidget";
import { getCSVConfig } from "../../../utils/csv";
import EmptyPageMessage from "../../../components/pages/customers/EmptyPageMessage";
import CustomerTag from "../../../components/CustomerTag";
import Button from "../../../components/Button";

const CustomersApplications = (props) => {
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";

    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(props.showNotification);

    const { data } = useSWR(
        `/api/customers/applications`,
        fetcher,
        {
            revalidateOnFocus: false
        },
    );

    // accountData is only used for the optional onboarding-link widget. Use
    // nonRedirectFetcher (not the local redirecting fetcher) so a 403 from users
    // without settings access degrades gracefully instead of triggering the global
    // 403 redirect to the dashboard. The applications fetch above keeps `fetcher`.
    const { data: accountData } = useSWR("/api/settings/account", nonRedirectFetcher, {
        revalidateOnFocus: false,
    });

    return (
        <CustomersPage {...props}>
            <Box gap="small">
                {(data?.pending && data.pending.length === 0 && data.recently_approved.length === 0) ? (
                    <EmptyPageMessage>
                        <Box>
                            <Box margin={{ bottom: "medium" }}>
                                <Heading level={2} size={isMobile ? "medium" : "large"}  margin={{vertical: 'small'}}>
                                    You have no pending applications
                                </Heading>
                                <Box width={{max: 'large'}}>
                                    <Text>Share this link with your customer for easy onboarding. <br/>They'll be able to add their details right into Streamlined.</Text>
                                </Box>
                            </Box>
                            {accountData?.slug && (
                                <Box width={{max: 'medium'}}>
                                    <CopyLinkWidget link={`${window.location.origin}/hi/${accountData?.slug}`} />
                                </Box>
                            )}
                        </Box>
                    </EmptyPageMessage>
                ) : (
                    <Box>
                        <PendingApprovalSection data={data} />
                        <RecentlyApprovedSection data={data} />
                    </Box>
                )}
            </Box>
        </CustomersPage>
    );
}

const PendingApprovalSection = ({data}) => {
    const router = useRouter();
    const [selectedApplications, setSelectedApplications] = useState([]);
    const notificationCtx = useContext(NotificationsContext);
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    return (
        <Box>
            <Box margin={{top: 'medium'}}>
                {data?.pending && data.pending.length > 0 && (
                    <DataTable
                        dataName="pending_applications"
                        dataTableWrapperProps={{
                            margin: undefined,
                            pad: undefined,
                            titleProps: { margin: {vertical: 'xsmall', horizontal: 'small'}},
                            collapsible: true,
                        }}
                        title={`Pending approval`}
                        controls={data && selectedApplications && !selectedApplications.length ? (
                            <Box margin={{horizontal: 'xsmall'}}>
                                <Text color="text-weak">{data?.pending.length} application{data?.pending.length > 1 ? 's' : ''}</Text>
                            </Box>
                        ) : ''}
                        icon={IconLoadingCircle}
                        emptyListMessage="You have no pending application"
                        data={data.pending}
                        columns={[{
                            selector: row => row.uuid,
                            cell: (datum) => {
                                return (
                                    <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                                        <Box data-tag="allowRowEvents">
                                            {datum?.company_name}
                                        </Box>
                                        <Text color="text-weak">Submitted on {formatDate(datum.application_submitted_at)}</Text>
                                    </Box>
                                )
                            },
                            name: 'Company name'
                        }]}
                        condensed
                        getFullData={() => {
                            return data?.pending;
                        }}
                        rowName={{plural: 'applications', single: 'application'}}
                        exportConfigs={getCSVConfig([
                            {name: 'Company Name', selector: 'company_name'},
                            {name: 'First name', selector: 'first_name'},
                            {name: 'Last name', selector: 'last_name'},
                            {name: 'Role in business', selector: 'role_at_business'},
                            {name: 'Phone number', selector: 'primary_phone'},
                            {name: 'Email', selector: 'email'},
                            {name: 'Submitted at', selector: 'application_submitted_at'},
                        ])}
                        selectableRows
                        onSelectedRowsChange={(data) => data?.selectedRows && setSelectedApplications(data.selectedRows)}
                        selectedControls={(
                            <DialogContext.Consumer>
                                {({showDialog}) => (
                                    <Box direction="row" gap="xsmall">
                                        <Button
                                            event={{section: 'applications_list', element: 'archive_applications'}}
                                            icon={<FontAwesomeIcon SelectedIcon={IconDelete} size="small" color="status-critical" />}
                                            label={"Archive"}
                                            gap="xsmall"
                                            onClick={() => {
                                                showDialog({
                                                    title: 'Archive applications',
                                                    message: `Are you sure you want to archive ${selectedApplications.length} customer${selectedApplications.length > 1 ? 's' : ''}? `,
                                                    callback: () => archiveCustomers(
                                                        selectedApplications.map(application => application.id),
                                                        notificationCtx.showNotification,
                                                        router.reload
                                                    ),
                                                    ctaType: 'error'
                                                })
                                            }}
                                            hoverIndicator
                                            align="end"
                                            secondary
                                        />
                                    </Box>
                                )}
                            </DialogContext.Consumer>
                        )}
                        noTableHead={isMobile || (data && !data?.pending.length < 2)}
                        onRowClicked={row => router.push(`/customers/applications/${row.uuid}`)}
                    />
                )}
            </Box>
        </Box>
    );
}

const RecentlyApprovedSection = ({data}) => {
    const { mutate } = useSWRConfig();
    const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
    const [emailTemplateData, setEmailTemplateData] = useState();
    const [emailFormValues, setEmailFormValues] = useState({});
    const notificationCtx = useContext(NotificationsContext);
    const [recipientId, setRecipientId] = useState();
    const size = useContext(ResponsiveContext);

    const openSendNotificationEmailModal = (id) => {
        fetch(`/api/customers/${id}/notify-template`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(handleErrors)
            .then((data) => {
                setEmailTemplateData(data);
                setEmailFormValues({
                    email_subject: data?.subject,
                    email_message: data?.message
                })
                setShowEmailPreviewModal(true);
            })
            .catch(notificationCtx.showNotification);
    }

    return data?.recently_approved && data.recently_approved.length > 0 ? (
        <Section
            collapsible
            title="Recently approved"
            icon={IconCircleCheck}>
            <List
                data={data.recently_approved}
                pad="0"
                margin="0">
                {(customer) => (
                    <Box
                        direction="row"
                        gap="xsmall"
                        align="center"
                        justify="between"
                        fill="horizontal"
                        border={{side: 'bottom', color: 'border-weak'}}
                        pad={{vertical: 'small'}}>
                        <Box align="start" gap="xxsmall" >
                            <CustomerTag id={customer?.id} inline />
                            <Text color="text-weak">Approved on {formatDate(customer.application_approved_at)}</Text>
                        </Box>
                        <Box gap="small" direction="row" align="center">
                            {size != "small" && (
                                customer?.application_notified_at ?
                                    (<Box direction="row" gap="xsmall" align="center" margin={{horizontal: 'xsmall'}}>
                                        <StatusIcon type="success" size="small" />
                                        <Text>Messaged</Text>
                                    </Box>) : (
                                        <Button 
                                            event={{section: 'applications_list', element: 'send_welcome_message', description: customer?.id}}
                                            label="Send welcome message" 
                                            secondary onClick={() => {
                                                setRecipientId(customer.uuid);
                                                openSendNotificationEmailModal(customer.uuid);
                                            }} />
                                    )
                            )}
                        </Box>
                    </Box>
                )}
            </List>
            {showEmailPreviewModal && (
                <PreviewEmailModal
                    onClose={() => setShowEmailPreviewModal(false)}
                    onSubmit={() => notifyCustomer(
                        {id: recipientId},
                        emailFormValues,
                        notificationCtx.showNotification,
                        () => {
                            setShowEmailPreviewModal(false);
                            mutate('/api/customers/applications');
                        }
                    )}
                    data={emailTemplateData}
                    formValues={emailFormValues}
                    onChange={(data) => setEmailFormValues(data)}
                    title={'Send welcome message'}
                    submitBtnText="Send email"
                    to={emailTemplateData?.to}
                    cc={emailTemplateData?.cc}
                />
            )}
        </Section>
    ) : (
        <></>
    );
}

export default CustomersApplications;
